import React from 'react';
import { Box, IconButton } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { moveItem } from './reorder-utils';

interface SortableListProps<T> {
    items: T[];
    getId: (item: T) => number;
    onReorder: (items: T[]) => void;
    renderItem: (item: T, dragHandle: React.ReactNode) => React.ReactNode;
    disabled?: boolean;
    /** Itens que não podem ser arrastados (ex.: menu Início, sempre o primeiro). */
    canDrag?: (item: T) => boolean;
}

function SortableRow({ id, disabled, children }: { id: number; disabled?: boolean; children: (dragHandle: React.ReactNode) => React.ReactNode }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

    const dragHandle = (
        <IconButton
            size="small"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            disabled={disabled}
            aria-label="Arrastar para reordenar"
            sx={{ cursor: disabled ? 'default' : 'grab', color: 'text.secondary', touchAction: 'none' }}
        >
            <DragIndicatorIcon fontSize="small" />
        </IconButton>
    );

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                position: 'relative',
                zIndex: isDragging ? 2 : 'auto',
                opacity: isDragging ? 0.85 : 1,
            }}
        >
            {children(dragHandle)}
        </Box>
    );
}

export function SortableList<T>({ items, getId, onReorder, renderItem, disabled, canDrag }: SortableListProps<T>) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const fromIndex = items.findIndex((item) => getId(item) === active.id);
        const toIndex = items.findIndex((item) => getId(item) === over.id);
        if (fromIndex < 0 || toIndex < 0) return;

        onReorder(moveItem(items, fromIndex, toIndex));
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(getId)} strategy={verticalListSortingStrategy}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {items.map((item) => (
                        <SortableRow key={getId(item)} id={getId(item)} disabled={disabled || (canDrag ? !canDrag(item) : false)}>
                            {(dragHandle) => renderItem(item, dragHandle)}
                        </SortableRow>
                    ))}
                </Box>
            </SortableContext>
        </DndContext>
    );
}
